import { useState, useEffect } from 'react';
import { api, extractErrorMessage } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/Toast';
import { Button } from '../../components/Button';
import { HelpTip } from '../../components/HelpTip';
import type { ColumnaCostoMl, CostosMlVista, FilaCostoMl } from './types';

// Tabla de costos de Mercado Libre por peso y precio (spec §2.1). Cada versión vale desde su fecha;
// las anteriores quedan guardadas. Sólo un administrador la cambia.

const COLUMNAS: Array<{ key: ColumnaCostoMl; label: string }> = [
  { key: 'cargoLt15000', label: 'Cargo fijo menos de $15.000' },
  { key: 'cargo15000a23999', label: 'Cargo fijo $15.000 a $23.999' },
  { key: 'cargo24000a32999', label: 'Cargo fijo $24.000 a $32.999' },
  { key: 'envio33000a49999', label: 'Envío $33.000 a $49.999' },
  { key: 'envioGte50000', label: 'Envío $50.000 o más' },
];

type Borrador = Array<{ kgDesde: number } & Record<ColumnaCostoMl, string>>;

const hoyAr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
const fechaCorta = (iso: string) => iso.split('-').reverse().join('/');
const kgTexto = (kg: number) => kg.toLocaleString('es-AR');

function aBorrador(filas: Array<{ kgDesde: number } & Record<ColumnaCostoMl, number | null>>): Borrador {
  return filas.map(f => ({
    kgDesde: f.kgDesde,
    ...Object.fromEntries(COLUMNAS.map(c => [c.key, f[c.key] == null ? '' : String(f[c.key])])) as Record<ColumnaCostoMl, string>,
  }));
}

export function CostosMlPanel() {
  const { user } = useAuth();
  const toast = useToast();
  const esAdmin = user?.role === 'admin';
  const [vista, setVista] = useState<CostosMlVista | null>(null);
  const [borrador, setBorrador] = useState<Borrador>([]);
  const [fecha, setFecha] = useState(hoyAr());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = (v: CostosMlVista) => {
    setVista(v);
    setBorrador(aBorrador(v.vigente?.filas ?? v.propuesta ?? []));
  };

  useEffect(() => {
    api.get<CostosMlVista>('/api/v3/precios/costos-ml').then(cargar).catch(e => setError(extractErrorMessage(e)));
  }, []);

  const cambiar = (i: number, key: ColumnaCostoMl, valor: string) =>
    setBorrador(b => b.map((f, j) => (j === i ? { ...f, [key]: valor } : f)));

  const guardar = async () => {
    const filas: FilaCostoMl[] = [];
    for (const f of borrador) {
      const fila = { kgDesde: f.kgDesde } as FilaCostoMl;
      for (const c of COLUMNAS) {
        const texto = f[c.key].trim();
        const v = Number(texto.replace(',', '.'));
        if (!texto || !Number.isFinite(v) || v < 0) {
          toast.addToast(`Falta el monto de «${c.label}» para ${kgTexto(f.kgDesde)} kg (un número de 0 o más)`, 'error');
          return;
        }
        fila[c.key] = v;
      }
      filas.push(fila);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) { toast.addToast('Elegí desde qué día vale la tabla', 'error'); return; }
    setGuardando(true);
    try {
      cargar(await api.put<CostosMlVista>('/api/v3/precios/costos-ml', { vigenteDesde: fecha, filas }));
      toast.addToast(`Tabla guardada: vale desde el ${fechaCorta(fecha)}`, 'success');
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setGuardando(false);
    }
  };

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!vista) return <p className="text-sm text-slate-400">Cargando…</p>;
  const hoy = hoyAr();

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Costos de Mercado Libre por peso y precio</h2>
        <HelpTip tips={[
          'Lo que cobra Mercado Libre por cada venta, según el precio y el peso del producto.',
          'Debajo de $33.000: un cargo fijo por venta (el envío lo paga el comprador). Desde $33.000: el envío gratis, que paga el vendedor.',
          'Como lo que cobra depende del precio, el precio sugerido se calcula, se mira en qué tramo cae y, si cae en otro, se vuelve a calcular con ese tramo hasta que coincide.',
          'Cuando Mercado Libre cambia sus tarifas, cargá la tabla nueva con la fecha desde la que vale: la anterior queda guardada.',
        ]} />
      </div>

      {vista.vigente ? (
        <p className="mb-3 text-sm text-slate-600">Vigente desde el {fechaCorta(vista.vigente.vigenteDesde)}. Para cambiarla, editá los montos y guardala con la fecha desde la que vale.</p>
      ) : (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          Todavía no hay tabla guardada: el precio sugerido se calcula como antes (envío por rango de peso + cargo fijo que informa Mercado Libre).
          Esta propuesta sale de las tarifas de envío y de los cargos fijos de Mercado Libre de hoy. Completá lo vacío, revisala con las tarifas que publica Mercado Libre antes de guardarla y tocá «Guardar nueva versión».
        </div>
      )}

      <p className="mb-3 text-xs text-slate-500">
        Cada publicación usa la fila de su peso; un peso justo en el límite (por ejemplo 0,5 kg) usa la fila de abajo (0,3 kg), como los rangos de Mercado Libre.
        Sin peso: debajo de $33.000 el precio se calcula igual (el cargo fijo no depende del envío); desde $33.000 no hay precio sugerido, porque el envío depende del peso.
        «Vale desde»: los precios desde ese día usan esta versión; una fecha futura espera a ese día y hasta entonces sigue la anterior.
      </p>

      {vista.versiones.length > 0 && (
        <ul aria-label="Versiones guardadas" className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          {vista.versiones.map(v => (
            <li key={v.vigenteDesde}>
              Desde el {fechaCorta(v.vigenteDesde)}{v.creadoPor ? ` (${v.creadoPor})` : ''}
              {v.vigenteDesde > hoy ? ' · todavía no vale' : v.vigenteDesde === vista.vigente?.vigenteDesde ? ' · vigente' : ' · anterior'}
            </li>
          ))}
        </ul>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-2 py-2">Peso desde</th>
              {COLUMNAS.map(c => <th key={c.key} className="px-2 py-2 text-right">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {borrador.map((f, i) => (
              <tr key={f.kgDesde} className="border-t border-slate-100">
                <td className="whitespace-nowrap px-2 py-1 font-mono text-xs">{kgTexto(f.kgDesde)} kg</td>
                {COLUMNAS.map(c => (
                  <td key={c.key} className="px-2 py-1 text-right">
                    <input aria-label={`${c.label}, desde ${kgTexto(f.kgDesde)} kg`} value={f[c.key]} disabled={!esAdmin} inputMode="decimal"
                      onChange={e => cambiar(i, c.key, e.target.value)}
                      className={`w-24 rounded border px-2 py-1 text-right font-mono text-sm focus:border-brand-400 focus:outline-none ${
                        f[c.key].trim() || !esAdmin ? 'border-slate-200 disabled:bg-slate-50 disabled:text-slate-500' : 'border-amber-300 bg-amber-50 text-amber-900'}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {esAdmin && (
        <div className="mt-4 flex flex-wrap items-end justify-end gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="costos-ml-fecha" className="text-xs font-medium uppercase tracking-wide text-slate-500">Vale desde</label>
            <input id="costos-ml-fecha" type="date" value={fecha} onChange={e => setFecha(e.target.value)}
              className="rounded border border-slate-200 bg-white px-3 py-1.5 text-sm focus:border-brand-400 focus:outline-none" />
          </div>
          <Button onClick={guardar} loading={guardando}>Guardar nueva versión</Button>
        </div>
      )}
    </div>
  );
}
